import axios from "axios";
import { getAuthToken } from "../utils/auth";
import {
  SolarPanel,
  SearchFilters,
  AuthData,
  LoginResponse,
  Supplier,
  Inventory,
  SearchLog,
  PanelRequest,
  SupplierRequest,
} from "../types";

let API_BASE = "https://api.solarlookup.com/api";

// API_BASE = "http://localhost:3010/api"; // For local development

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post("/auth/login", { email, password });
    return response.data;
  },

  supplierLogin: async (email: string, password: string): Promise<AuthData> => {
    const response = await api.post("/auth/supplier/login", {
      email,
      password,
    });
    return response.data;
  },

  setSupplierPassword: async (
    token: string,
    password: string
  ): Promise<AuthData> => {
    const response = await api.post("/auth/supplier/set-password", {
      token,
      password,
    });
    return response.data;
  },

  signup: async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    companyName?: string;
  }) => {
    const response = await api.post("/auth/signup", userData);
    return response.data;
  },

  verifyEmail: async (userId: string, otpCode: string) => {
    const response = await api.post("/auth/verify-email", { userId, otpCode });
    return response.data;
  },

  resendOTP: async (userId: string) => {
    const response = await api.post("/auth/resend-otp", { userId });
    return response.data;
  },

  getSearchStats: async () => {
    const response = await api.get("/auth/search-stats");
    return response.data;
  },

  forgotPassword: async (email: string, userType?: string) => {
    const response = await api.post("/auth/forgot-password", {
      email,
      userType,
    });
    return response.data;
  },

  resetPassword: async (
    email: string,
    otpCode: string,
    newPassword: string
  ) => {
    const response = await api.post("/auth/reset-password", {
      email,
      otpCode,
      newPassword,
    });
    return response.data;
  },
};

export const inventoryAPI = {
  getAllInventory: () => api.get("/admin/inventory").then((res) => res.data),
  getInventoryByPanel: (panelId: string) =>
    api.get(`/admin/inventory/panel/${panelId}`).then((res) => res.data),
  getInventoryBySupplier: (supplierId: string) =>
    api.get(`/admin/inventory/supplier/${supplierId}`).then((res) => res.data),
  addInventory: (data: {
    panelId: string;
    supplierId: string;
    quantity: number;
    pricePerUnit?: number;
  }) => api.post("/admin/inventory", data).then((res) => res.data),
  updateInventory: (
    id: string,
    data: { quantity?: number; pricePerUnit?: number }
  ) => api.put(`/admin/inventory/${id}`, data).then((res) => res.data),
  deleteInventory: (id: string) => api.delete(`/admin/inventory/${id}`),
};

export const solarAPI = {
  create: async (
    panel: Omit<SolarPanel, "id" | "createdAt" | "updatedAt">
  ): Promise<SolarPanel> => {
    const response = await api.post("/solars/create", panel);
    return response.data;
  },

  uploadPDF: async (file: File, force: boolean = false): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("force", force.toString());
    const response = await api.post("/solars/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  search: async (
    filters: SearchFilters
  ): Promise<{
    panels: SolarPanel[];
    searchLimit?: {
      maxSearches: number;
      remainingSearches: number;
      limitReached: boolean;
    };
  }> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
    const response = await api.get(`/solars/search?${params.toString()}`);

    // Handle both old and new response formats
    if (response.data.panels) {
      return response.data;
    } else {
      // Legacy format - just panels array
      return { panels: response.data };
    }
  },

  getCellTypes: async (): Promise<string[]> => {
    const response = await api.get("/solars/cell-types");
    return response.data;
  },
};

export const supplierAPI = {
  getInventory: async (): Promise<Inventory[]> => {
    const response = await api.get("/supplier/inventory");
    return response.data;
  },

  addInventory: async (data: {
    panelId: string;
    quantity: number;
    pricePerUnit?: number;
  }): Promise<Inventory> => {
    const response = await api.post("/supplier/inventory", data);
    return response.data;
  },

  updateInventory: async (
    id: string,
    data: { quantity?: number; pricePerUnit?: number }
  ): Promise<Inventory> => {
    const response = await api.put(`/supplier/inventory/${id}`, data);
    return response.data;
  },

  deleteInventory: async (id: string): Promise<void> => {
    await api.delete(`/supplier/inventory/${id}`);
  },
};

export const panelRequestAPI = {
  requestPanel: async (panelData: any): Promise<PanelRequest> => {
    const isFormData = panelData instanceof FormData;
    const response = await api.post("/panel-requests", panelData, {
      headers: isFormData
        ? { "Content-Type": "multipart/form-data" }
        : undefined,
    });
    return response.data;
  },

  parsePdf: async (pdfFile: File): Promise<any> => {
    const formData = new FormData();
    formData.append("attachment", pdfFile);
    const response = await api.post("/panel-requests/parse-pdf", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  submitMultiplePanelRequests: async (
    panelRequests: any[],
    originalFileName: string,
    attachment?: File
  ): Promise<any> => {
    const formData = new FormData();
    formData.append("panelRequests", JSON.stringify(panelRequests));
    formData.append("originalFileName", originalFileName);

    if (attachment) {
      formData.append("attachment", attachment);
    }

    const response = await api.post("/panel-requests/multiple", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  getUserRequests: async (): Promise<PanelRequest[]> => {
    const response = await api.get("/panel-requests/my");
    return response.data;
  },

  getAttachment: async (requestId: string): Promise<Blob> => {
    const response = await api.get(`/panel-requests/${requestId}/attachment`, {
      responseType: "blob",
    });
    return response.data;
  },
};

export const adminAPI = {
  getSearchLogs: async (
    page: number = 1,
    limit: number = 50,
    filters?: {
      startDate?: string;
      endDate?: string;
      hasResults?: boolean;
      minResults?: number;
      maxResults?: number;
      searchQuery?: string;
      ipAddress?: string;
    }
  ): Promise<{
    logs: SearchLog[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    appliedFilters?: any;
  }> => {
    let url = `/admin/search-logs?page=${page}&limit=${limit}`;

    if (filters) {
      const params = new URLSearchParams();
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      if (filters.hasResults !== undefined)
        params.append("hasResults", filters.hasResults.toString());
      if (filters.minResults !== undefined)
        params.append("minResults", filters.minResults.toString());
      if (filters.maxResults !== undefined)
        params.append("maxResults", filters.maxResults.toString());
      if (filters.searchQuery)
        params.append("searchQuery", filters.searchQuery);
      if (filters.ipAddress) params.append("ipAddress", filters.ipAddress);

      if (params.toString()) {
        url += `&${params.toString()}`;
      }
    }

    const response = await api.get(url);
    return response.data;
  },

  getSearchAnalytics: async (filters?: {
    startDate?: string;
    endDate?: string;
  }): Promise<{
    totalSearches: number;
    weeklySearches: number;
    monthlySearches: number;
    noResultSearches: number;
    customRangeSearches?: number;
    customRangeNoResults?: number;
    avgResultsPerSearch: number;
    noResultPercentage: number;
    appliedFilters?: any;
  }> => {
    let url = "/admin/search-analytics";

    if (filters) {
      const params = new URLSearchParams();
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }

    const response = await api.get(url);
    return response.data;
  },

  getSuppliers: async (): Promise<Supplier[]> => {
    const response = await api.get("/admin/suppliers");
    return response.data;
  },

  createSupplier: async (
    data: Omit<Supplier, "id" | "createdAt" | "isActive">
  ): Promise<Supplier> => {
    const response = await api.post("/admin/suppliers", data);
    return response.data;
  },

  toggleSupplierStatus: async (id: string): Promise<Supplier> => {
    const response = await api.put(`/admin/suppliers/${id}/toggle`);
    return response.data;
  },

  getPanelRequests: async (): Promise<PanelRequest[]> => {
    const response = await api.get("/admin/panel-requests");
    return response.data;
  },

  updatePanelRequestStatus: async (
    id: string,
    status: string,
    adminNotes?: string
  ): Promise<PanelRequest> => {
    const response = await api.put(`/admin/panel-requests/${id}`, {
      status,
      adminNotes,
    });
    return response.data;
  },

  validatePanelRequest: async (
    id: string
  ): Promise<{
    isValid: boolean;
    missingFields: string[];
    warnings: string[];
  }> => {
    const response = await api.get(`/admin/panel-requests/${id}/validate`);
    return response.data;
  },

  // Supplier Request Management
  getSupplierRequests: async (): Promise<SupplierRequest[]> => {
    const response = await api.get("/admin/supplier-requests");
    return response.data;
  },

  updateSupplierRequestStatus: async (
    id: string,
    status: string,
    adminNotes?: string
  ): Promise<SupplierRequest> => {
    const response = await api.put(`/admin/supplier-requests/${id}`, {
      status,
      adminNotes,
    });
    return response.data;
  },

  deleteSupplierRequest: async (id: string): Promise<void> => {
    await api.delete(`/admin/supplier-requests/${id}`);
  },
};

export const adminPanelAPI = {
  getAllPanels: async (
    page: number = 1,
    limit: number = 50,
    filters?: {
      search?: string;
      CellType?: string;
      minWatts?: number;
      maxWatts?: number;
      minEfficiency?: number;
      maxEfficiency?: number;
    }
  ): Promise<{
    panels: SolarPanel[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    appliedFilters?: any;
  }> => {
    let url = `/admin/panels?page=${page}&limit=${limit}`;

    if (filters) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value.toString());
        }
      });
      if (params.toString()) {
        url += `&${params.toString()}`;
      }
    }

    const response = await api.get(url);
    return response.data;
  },

  getPanelById: async (id: string): Promise<SolarPanel> => {
    const response = await api.get(`/admin/panels/${id}`);
    return response.data;
  },

  updatePanel: async (
    id: string,
    panel: Partial<SolarPanel>
  ): Promise<SolarPanel> => {
    const response = await api.put(`/admin/panels/${id}`, panel);
    return response.data;
  },

  deletePanel: async (id: string): Promise<SolarPanel> => {
    const response = await api.delete(`/admin/panels/${id}`);
    return response.data;
  },

  restorePanel: async (id: string): Promise<SolarPanel> => {
    const response = await api.put(`/admin/panels/${id}/restore`);
    return response.data;
  },
};

export const supplierRequestAPI = {
  createRequest: async (
    data: Omit<
      SupplierRequest,
      "id" | "status" | "adminNotes" | "expiresAt" | "created_at"
    > & { recaptchaToken: string }
  ): Promise<{
    id: string;
    email: string;
    tempPassword: string;
    message: string;
  }> => {
    const response = await api.post("/supplier-requests", data);
    return response.data;
  },
};

export const solarMonitoringAPI = {
  getAlertRules: async () => {
    const response = await api.get("/admin/alert-rules");
    return response.data;
  },

  getAlertRuleById: async (id: string) => {
    const response = await api.get(`/admin/alert-rules/${id}`);
    return response.data;
  },

  createAlertRule: async (ruleData: any) => {
    const response = await api.post("/admin/alert-rules", ruleData);
    return response.data;
  },

  updateAlertRule: async (id: string, ruleData: any) => {
    const response = await api.put(`/admin/alert-rules/${id}`, ruleData);
    return response.data;
  },

  deleteAlertRule: async (id: string) => {
    const response = await api.delete(`/admin/alert-rules/${id}`);
    return response.data;
  },

  toggleAlertRule: async (id: string) => {
    const response = await api.put(`/admin/alert-rules/${id}/toggle`);
    return response.data;
  },

  getAlertHistory: async (params?: {
    limit?: number;
    offset?: number;
    ruleId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const response = await api.get(
      `/admin/alert-history${
        queryParams.toString() ? "?" + queryParams.toString() : ""
      }`
    );
    return response.data;
  },

  getCurrentViolations: async () => {
    const response = await api.get("/admin/alert-violations");
    return response.data;
  },

  getMonitoringStatus: async () => {
    const response = await api.get("/admin/monitoring-status");
    return response.data;
  },

  testAlertEmail: async (emails: string[]) => {
    const response = await api.post("/admin/test-alert-email", { emails });
    return response.data;
  },
};
